import { useEffect, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { KeyRound } from "lucide-react";
import { authApi, type ResetKeyInfo } from "../api/authApi";
import { HttpError } from "../api/http";
import { Card } from "../components/layout/Card";

const MIN = 8;
const MAX = 50;

/**
 * Self-service password reset, step 2 (also used by the welcome mail):
 * validates the key from the link, then lets the user choose a password.
 */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const key = (params.get("key") ?? "").trim();
  const [info, setInfo] = useState<ResetKeyInfo | null>(null);
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let active = true;
    if (!key) {
      setInfo({ valid: false });
      return;
    }
    authApi
      .validateResetKey(key)
      .then((i) => active && setInfo(i))
      .catch(() => active && setInfo({ valid: false }));
    return () => {
      active = false;
    };
  }, [key]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    if (password.length < MIN) return setError(`Use at least ${MIN} characters.`);
    if (password.length > MAX) return setError(`Use at most ${MAX} characters.`);
    if (password !== repeat) return setError("The passwords don't match.");
    setSubmitting(true);
    try {
      await authApi.resetPassword(key, password);
      setDone(true);
    } catch (err) {
      if (err instanceof HttpError && err.message === "invalid_key") setInfo({ valid: false });
      else setError(err instanceof Error ? err.message : "Could not change the password.");
    } finally {
      setSubmitting(false);
    }
  };

  const invalid = (
    <>
      <p className="sub">This link is invalid or has expired. Request a new one to choose a password.</p>
      <Link to="/forgot-password" className="btn btn-primary" style={{ textDecoration: "none" }}>
        Request a new link
      </Link>
    </>
  );

  return (
    <section className="auth-shell">
      <Card>
        <div className="auth-card">
          <h2>{done ? "Password changed" : "Choose a password"}</h2>
          {info == null ? (
            <p className="sub">Checking your link…</p>
          ) : done ? (
            <>
              <p className="sub">Your password has been changed. You can now sign in with it.</p>
              <Link to="/login" className="btn btn-primary" style={{ textDecoration: "none" }}>
                Sign in
              </Link>
            </>
          ) : !info.valid ? (
            invalid
          ) : (
            <>
              <p className="sub">
                Choose a new password for <strong>{info.username}</strong>
                {info.portalName ? <> on {info.portalName}</> : null}.
              </p>
              {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
              <form onSubmit={handleSubmit}>
                <div className="field">
                  <label htmlFor="password">New password</label>
                  <input
                    id="password"
                    type="password"
                    autoComplete="new-password"
                    minLength={MIN}
                    maxLength={MAX}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoFocus
                  />
                  <div className="hint">{MIN} to {MAX} characters.</div>
                </div>
                <div className="field">
                  <label htmlFor="repeat">Repeat password</label>
                  <input
                    id="repeat"
                    type="password"
                    autoComplete="new-password"
                    minLength={MIN}
                    maxLength={MAX}
                    value={repeat}
                    onChange={(e) => setRepeat(e.target.value)}
                    required
                  />
                </div>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  <KeyRound size={16} /> {submitting ? "Saving…" : "Set password"}
                </button>
              </form>
            </>
          )}
        </div>
      </Card>
    </section>
  );
}
